/**
 * JoinFooter — the mobile action bar shared by the join flow's number, code and
 * name screens.
 *
 * Fixed to the bottom of the screen and lifted above the software keyboard by
 * useKeyboardPinnedFooter (see that file for why iOS needs JS to do this). The
 * three screens previously each carried their own in-flow footer, which iOS
 * Safari hid behind the keyboard; they also carried three copies of the same
 * styles.
 *
 * Mobile only: the desktop layouts render their button inside the card, and this
 * bar is display:none from the `sm` breakpoint up.
 *
 * Pass the button (and any caption) as children, and reserve `footerHeight` as
 * bottom padding on the screen's scrolling column so content is never hidden
 * under this bar. Give the button itself `onMouseDown={e => e.preventDefault()}`:
 * iOS fires blur before click, so without it the first tap only closes the
 * keyboard and the footer moves out from under the finger.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { brand, spacing } from '../../style/splytBrand';

const useStyles = makeStyles((theme) => ({
  bar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    boxSizing: 'border-box',
    backgroundColor: brand.marsh,
    padding: `${spacing.sm}px ${spacing.md + 4}px`,
    // Clears the home indicator while the keyboard is closed.
    paddingBottom: `calc(${spacing.lg}px + env(safe-area-inset-bottom, 0px))`,
    // No transition: iOS reports the keyboard only once its animation has
    // finished, so easing here would add lag on top of that.
    willChange: 'transform',
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },

  // Above the keyboard the home-indicator inset does not apply, and a hairline
  // makes the bar read as part of the keyboard rather than floating over text.
  barPinned: {
    paddingBottom: spacing.sm,
    boxShadow: `0 -1px 0 ${brand.marshNeutral}`,
  },
}));

export default function JoinFooter({ footerRef, pinned, children }) {
  const classes = useStyles();
  return (
    <div ref={footerRef} className={`${classes.bar} ${pinned ? classes.barPinned : ''}`}>
      {children}
    </div>
  );
}

JoinFooter.propTypes = {
  footerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  pinned: PropTypes.bool,
  children: PropTypes.node,
};

JoinFooter.defaultProps = {
  pinned: false,
  children: null,
};
